import { Box, Tooltip } from 'portal-commons';
import { FunctionComponent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { faChartNetwork } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid } from '@material-ui/core';

import useControllers from './useControllers';
import { BrandRelationshipFormValues } from '../../types';
import { BRAND_RELATIONSHIP_SCORE } from '@/constants';
import { EntityTypes } from '@/shared_elements/enums';

interface BrandRelationshipFromProps {
  entityType?: string;
  defaultValues?: BrandRelationshipFormValues;
  onChange?: (values: BrandRelationshipFormValues) => void;
  onPrevious?: () => void;
  onNext?: () => void;
}

const BrandRelationshipForm: FunctionComponent<BrandRelationshipFromProps> = ({
  entityType = '',
  defaultValues,
  onChange,
  onPrevious,
  onNext,
}) => {
  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
    watch,
  } = useForm<BrandRelationshipFormValues>({
    defaultValues,
  });
  const {
    brandRelationshipScore,
    setBrandRelationshipScore,
    brandRelationshipScoreError,
  } = useControllers(control);

  const handleValid = () => {
    onNext?.();
  };

  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      onChange?.(value as BrandRelationshipFormValues);
    });
    return () => unsubscribe();
  }, [watch]);

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <Grid
        className="csp-portal"
        container
        justifyContent="flex-start"
        spacing={0}
      >
        <Grid item lg={12}>
          <div className="csp-verification-form brands">
            <div className="csp-details brandRelationship">
              <div className="csp-details-heading">
                <Box flexDirection="row" alignItems="center">
                  <Box margin={{ right: 's' }}>
                    <FontAwesomeIcon icon={faChartNetwork} size="lg" />
                  </Box>
                  <div className="title">
                    <h3 className="heading1">Brand Relationship</h3>
                    <Tooltip
                      title={
                        'The “Brand Relationship” reflects the business history you have with a specific brand.'
                      }
                      style={{ top: 1 }}
                    />
                  </div>
                </Box>
              </div>
              <Grid
                container
                justifyContent={'space-between'}
                className="brand-relationship-wrapper"
                data-testid="brandTrustScore"
              >
                {BRAND_RELATIONSHIP_SCORE.map((item, index) => (
                  <Grid
                    item
                    xs={2}
                    key={item.value}
                    className={`relationship-item${
                      brandRelationshipScore === item.value
                        ? ' active'
                        : entityType === EntityTypes.SoleProprietor && index > 0
                        ? ' disabledOption'
                        : ''
                    }`}
                    onClick={() => setBrandRelationshipScore(item.value)}
                  >
                    <img src={item.img} alt="" />
                    <div className="top-blk">
                      <p className="paragraph title">{item.label}</p>
                    </div>
                    <div className="content-blk">
                      <p className="paragraph">{item.description}</p>
                    </div>
                  </Grid>
                ))}
              </Grid>
              {!!brandRelationshipScoreError?.message && (
                <h6
                  className="error-msg-brand-relationship"
                  style={{ marginTop: '12px' }}
                >
                  {brandRelationshipScoreError.message}
                </h6>
              )}
            </div>
          </div>
        </Grid>
        <Grid item xs={12} className="bottom-blk">
          <ul className="list-inline">
            <li>
              <p>
                <a
                  onClick={onPrevious}
                  className={
                    isSubmitting ? 'secondary-btn disabled' : 'secondary-btn'
                  }
                >
                  Previous
                </a>
              </p>
            </li>
            <li className="form-group-field">
              <input
                type="submit"
                disabled={
                  isSubmitting ||
                  Object.values(errors).some((error) => !!error?.message)
                }
                value="Next"
              />
            </li>
          </ul>
        </Grid>
      </Grid>
    </form>
  );
};

export default BrandRelationshipForm;
